import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  GraduationCap, Loader2, LogOut, Calendar, Clock, Users, Play, Square,
  ClipboardList, MessageSquare, Save, Check, X, AlertCircle, AlertTriangle,
  CheckCircle2, History, FileText, Plus, RefreshCw, Search, UserPlus, Trash2, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

// Obtener el estado del horario para hoy
const getEstadoHorarioHoy = (horarios) => {
  if (!horarios || horarios.length === 0) return null;
  
  const ahora = new Date();
  // JavaScript: 0=Domingo, 1=Lunes... Nuestro sistema: 0=Lunes, 1=Martes...
  const diaJS = ahora.getDay();
  const diaHoy = diaJS === 0 ? 6 : diaJS - 1; // Convertir a nuestro formato
  
  const horarioHoy = horarios.find(h => h.dia === diaHoy);
  if (!horarioHoy) return null;
  
  const [horaInicio, minInicio] = horarioHoy.hora_inicio.split(':').map(Number);
  const horaInicioDate = new Date();
  horaInicioDate.setHours(horaInicio, minInicio, 0, 0);
  
  if (ahora < horaInicioDate) {
    return { tipo: 'por_comenzar', hora: horarioHoy.hora_inicio };
  } else {
    return { tipo: 'con_retraso', hora: horarioHoy.hora_inicio };
  }
};

export default function EntrenadorDashboard() {
  const { academiaId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [academia, setAcademia] = useState(null);
  const [entrenador, setEntrenador] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [clasesAbiertas, setClasesAbiertas] = useState([]);
  const [historialClases, setHistorialClases] = useState([]);
  const [activeTab, setActiveTab] = useState('categorias');
  const [filtroEnCurso, setFiltroEnCurso] = useState('');
  const [filtroParaAbrir, setFiltroParaAbrir] = useState('');
  
  // Modal clase
  const [showClaseModal, setShowClaseModal] = useState(false);
  const [claseActiva, setClaseActiva] = useState(null);
  const [asistenciasTemp, setAsistenciasTemp] = useState({});
  const [notaTemp, setNotaTemp] = useState('');
  const [tipoNotaTemp, setTipoNotaTemp] = useState('observacion');
  const [horaFinTemp, setHoraFinTemp] = useState('');
  const [showAgregarEntrenador, setShowAgregarEntrenador] = useState(false);
  const [entrenadoresDisponibles, setEntrenadoresDisponibles] = useState([]);
  
  // Editar notas
  const [editandoNotaId, setEditandoNotaId] = useState(null);
  const [editNotaContenido, setEditNotaContenido] = useState('');
  const [editNotaTipo, setEditNotaTipo] = useState('');

  const TIPOS_NOTA = [
    { value: 'observacion', label: 'Observación', color: 'bg-blue-600' },
    { value: 'avance', label: 'Avance', color: 'bg-emerald-600' },
    { value: 'incidente', label: 'Incidente', color: 'bg-red-600' },
    { value: 'actividad', label: 'Actividad Realizada', color: 'bg-purple-600' },
    { value: 'general', label: 'General', color: 'bg-slate-600' }
  ];

  useEffect(() => {
    checkAccess();
  }, [academiaId]);

  const checkAccess = async () => {
    try {
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(meRes.data);
      
      const accesoRes = await axios.get(`${API}/academia/${academiaId}/mi-acceso`, { withCredentials: true });
      
      if (!accesoRes.data.tiene_acceso) {
        toast.error('No tienes acceso a esta academia');
        navigate(`/academia/${academiaId}/login`);
        return;
      }
      
      setAcademia(accesoRes.data.academia);
      setEntrenador(accesoRes.data.entrenador);
      
      await loadCategorias();
      await loadClasesAbiertas();
      await loadHistorial();
    } catch (err) {
      navigate(`/academia/${academiaId}/login`);
    } finally {
      setLoading(false);
    }
  };

  const loadCategorias = async () => {
    try {
      const res = await axios.get(`${API}/academias/${academiaId}/categorias`, { withCredentials: true });
      setCategorias(res.data.categorias || res.data || []);
    } catch (err) {
      console.error('Error loading categorias:', err);
    }
  };

  const loadClasesAbiertas = async () => {
    try {
      const res = await axios.get(`${API}/academias/${academiaId}/clases-sesion?estado=abierta`, { withCredentials: true });
      setClasesAbiertas(res.data.clases || []);
    } catch (err) {
      console.error('Error loading clases abiertas:', err);
    }
  };

  const loadHistorial = async () => {
    try {
      const res = await axios.get(`${API}/academias/${academiaId}/clases-sesion`, { withCredentials: true });
      setHistorialClases(res.data.clases || []);
    } catch (err) {
      console.error('Error loading historial:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate(`/academia/${academiaId}/login`);
  };

  const handleIniciarClase = async (categoriaId) => {
    try {
      const res = await axios.post(`${API}/academias/${academiaId}/clases-sesion`, {
        categoria_id: categoriaId
      }, { withCredentials: true });
      toast.success('Clase iniciada');
      loadCategorias();
      loadClasesAbiertas();
      handleAbrirClase(res.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar clase');
    }
  };

  const handleAbrirClase = async (claseId) => {
    try {
      const res = await axios.get(`${API}/academias/${academiaId}/clases-sesion/${claseId}`, { withCredentials: true });
      setClaseActiva(res.data);
      setHoraFinTemp(res.data.hora_fin || '');
      const asistInit = {};
      res.data.alumnos?.forEach(al => {
        // Por defecto ausente (false), a menos que ya tenga un valor registrado
        asistInit[al.id] = al.presente !== null ? al.presente : false;
      });
      setAsistenciasTemp(asistInit);
      
      // Cargar entrenadores disponibles
      try {
        const entRes = await axios.get(`${API}/academias/${academiaId}/entrenadores`, { withCredentials: true });
        setEntrenadoresDisponibles(entRes.data.entrenadores || []);
      } catch (e) {
        setEntrenadoresDisponibles([]);
      }
      
      setShowClaseModal(true);
    } catch (error) {
      toast.error('Error al cargar clase');
    }
  };

  const handleCerrarClase = async () => {
    if (!claseActiva) return;
    try {
      await axios.put(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}/cerrar`, {
        hora_fin: horaFinTemp || undefined
      }, { withCredentials: true });
      toast.success('Clase cerrada');
      setShowClaseModal(false);
      setClaseActiva(null);
      setHoraFinTemp('');
      loadCategorias();
      loadClasesAbiertas();
      loadHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar clase');
    }
  };

  const handleGuardarHoraFin = async () => {
    if (!claseActiva || !horaFinTemp) return;
    try {
      await axios.put(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}`, {
        hora_fin: horaFinTemp
      }, { withCredentials: true });
      setClaseActiva({ ...claseActiva, hora_fin: horaFinTemp });
      toast.success('Hora de término guardada');
    } catch (error) {
      toast.error('Error al guardar hora');
    }
  };

  const handleAgregarEntrenadorClase = async (entrenadorId) => {
    if (!claseActiva) return;
    try {
      await axios.post(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}/agregar-entrenador`, {
        entrenador_id: entrenadorId
      }, { withCredentials: true });
      // Recargar clase
      const res = await axios.get(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}`, { withCredentials: true });
      setClaseActiva(res.data);
      setShowAgregarEntrenador(false);
      toast.success('Entrenador agregado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar entrenador');
    }
  };

  const handleQuitarEntrenadorClase = async (entrenadorId) => {
    if (!claseActiva) return;
    try {
      await axios.delete(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}/entrenador/${entrenadorId}`, { withCredentials: true });
      // Recargar clase
      const res = await axios.get(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}`, { withCredentials: true });
      setClaseActiva(res.data);
      toast.success('Entrenador removido');
    } catch (error) {
      toast.error('Error al quitar entrenador');
    }
  };

  const handleGuardarAsistencia = async () => {
    if (!claseActiva) return;
    try {
      const asistencias = Object.entries(asistenciasTemp).map(([alumno_id, presente]) => ({
        alumno_id, presente
      }));
      await axios.post(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}/asistencia-multiple`, {
        asistencias
      }, { withCredentials: true });
      toast.success('Asistencia guardada');
      handleAbrirClase(claseActiva.id);
    } catch (error) {
      toast.error('Error al guardar asistencia');
    }
  };

  const handleAgregarNota = async () => {
    if (!claseActiva || !notaTemp.trim()) return;
    try {
      await axios.post(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}/notas`, {
        contenido: notaTemp,
        tipo: tipoNotaTemp
      }, { withCredentials: true });
      toast.success('Nota agregada');
      setNotaTemp('');
      setTipoNotaTemp('observacion');
      handleAbrirClase(claseActiva.id);
    } catch (error) {
      toast.error('Error al agregar nota');
    }
  };

  const handleEditarNota = async (notaId) => {
    if (!editNotaContenido.trim()) return;
    try {
      await axios.put(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}/notas/${notaId}`, {
        contenido: editNotaContenido,
        tipo: editNotaTipo
      }, { withCredentials: true });
      toast.success('Nota actualizada');
      setEditandoNotaId(null);
      handleAbrirClase(claseActiva.id);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al editar nota';
      toast.error(msg);
    }
  };

  const handleEliminarNota = async (notaId) => {
    if (!confirm('¿Estás seguro de eliminar esta nota?')) return;
    try {
      await axios.delete(`${API}/academias/${academiaId}/clases-sesion/${claseActiva.id}/notas/${notaId}`, { withCredentials: true });
      toast.success('Nota eliminada');
      handleAbrirClase(claseActiva.id);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar nota';
      toast.error(msg);
    }
  };

  const iniciarEdicionNota = (nota) => {
    setEditandoNotaId(nota.id);
    setEditNotaContenido(nota.contenido);
    setEditNotaTipo(nota.tipo);
  };

  const cancelarEdicionNota = () => {
    setEditandoNotaId(null);
    setEditNotaContenido('');
    setEditNotaTipo('');
  };

  // Verificar si el usuario es el dueño de la nota
  const esAutorNota = (nota) => {
    return entrenador && nota.entrenador_id === entrenador.id;
  };

  // Verificar si el usuario es el entrenador principal de la clase
  const esEntrenadorPrincipal = () => {
    return entrenador && claseActiva && entrenador.id === claseActiva.entrenador_principal_id;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header - Mobile Optimized */}
      <header className="bg-slate-800 border-b border-slate-700 px-3 sm:px-4 py-3 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {academia?.logo ? (
              <img src={academia.logo} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="font-bold text-white text-sm sm:text-base truncate">{academia?.nombre}</h1>
              <p className="text-xs text-emerald-400 truncate">{entrenador?.nombre} {entrenador?.apellido}</p>
            </div>
          </div>
          
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white flex-shrink-0 px-2 sm:px-3">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Salir</span>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto p-3 sm:p-4">
        {/* Tabs - Mobile Optimized */}
        <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2">
          <Button
            variant={activeTab === 'categorias' ? 'default' : 'outline'}
            onClick={() => setActiveTab('categorias')}
            className={`flex-shrink-0 text-xs sm:text-sm ${activeTab === 'categorias' ? 'bg-emerald-600' : 'border-slate-600 text-slate-300'}`}
          >
            <Calendar className="w-4 h-4 sm:mr-2" /> 
            <span className="hidden sm:inline">Abrir Clase</span>
            <span className="sm:hidden">Clases</span>
            {clasesAbiertas.length > 0 && (
              <span className="ml-1 sm:ml-2 bg-amber-500 text-white text-xs px-1.5 sm:px-2 py-0.5 rounded-full">{clasesAbiertas.length}</span>
            )}
          </Button>
          <Button
            variant={activeTab === 'historial' ? 'default' : 'outline'}
            onClick={() => { setActiveTab('historial'); loadHistorial(); }}
            className={`flex-shrink-0 text-xs sm:text-sm ${activeTab === 'historial' ? 'bg-emerald-600' : 'border-slate-600 text-slate-300'}`}
          >
            <History className="w-4 h-4 sm:mr-2" /> 
            <span className="hidden sm:inline">Historial</span>
          </Button>
        </div>

        {/* Categorías - Abrir Clase */}
        {activeTab === 'categorias' && (
          <div className="space-y-6 sm:space-y-8">
            {/* ========== CLASES EN CURSO ========== */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                  <Play className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                  <span className="hidden sm:inline">Clases en Curso</span>
                  <span className="sm:hidden">En Curso</span>
                  {clasesAbiertas.length > 0 && (
                    <Badge className="bg-emerald-600 ml-1 sm:ml-2">{clasesAbiertas.length}</Badge>
                  )}
                </h2>
                <Button variant="outline" size="sm" onClick={() => { loadCategorias(); loadClasesAbiertas(); }} className="border-slate-600 text-slate-300 p-2 sm:px-3">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {clasesAbiertas.length === 0 ? (
                <Card className="p-4 sm:p-6 bg-slate-800/50 border-slate-700 text-center">
                  <p className="text-slate-500 text-sm">No hay clases en curso</p>
                </Card>
              ) : (
                <>
                  {/* Filtro Clases en Curso */}
                  <div className="relative mb-3 sm:mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Filtrar clases..."
                      value={filtroEnCurso}
                      onChange={(e) => setFiltroEnCurso(e.target.value)}
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 text-sm"
                    />
                  </div>
                  
                  <Card className="bg-slate-800 border-emerald-700/50 overflow-hidden">
                    <div className="divide-y divide-slate-700">
                      {clasesAbiertas
                        .filter(clase => clase.categoria_nombre?.toLowerCase().includes(filtroEnCurso.toLowerCase()))
                        .map((clase) => (
                          <div key={clase.id} className="p-3 sm:p-4 hover:bg-emerald-900/20 cursor-pointer active:bg-emerald-900/30" onClick={() => handleAbrirClase(clase.id)}>
                            <div className="flex justify-between items-center gap-2">
                              <div className="min-w-0">
                                <p className="font-medium text-white text-sm sm:text-base truncate">{clase.categoria_nombre}</p>
                                <p className="text-xs sm:text-sm text-slate-400">{clase.fecha} • {clase.hora_inicio}</p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Badge className="bg-emerald-600 text-xs">En Curso</Badge>
                                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </Card>
                </>
              )}
            </div>
            
            {/* ========== CLASES PARA ABRIR ========== */}
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
                <span className="hidden sm:inline">Clases para Abrir</span>
                <span className="sm:hidden">Para Abrir</span>
                {categorias.filter(cat => !clasesAbiertas.find(c => c.categoria_id === cat.id)).length > 0 && (
                  <Badge className="bg-amber-600 ml-1 sm:ml-2">
                    {categorias.filter(cat => !clasesAbiertas.find(c => c.categoria_id === cat.id)).length}
                  </Badge>
                )}
              </h2>
              
              {categorias.filter(cat => !clasesAbiertas.find(c => c.categoria_id === cat.id)).length === 0 ? (
                <Card className="p-4 sm:p-6 bg-slate-800/50 border-slate-700 text-center">
                  <p className="text-slate-500 text-sm">Todas las categorías tienen clases abiertas</p>
                </Card>
              ) : (
                <>
                  {/* Filtro Clases para Abrir */}
                  <div className="relative mb-3 sm:mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Filtrar categorías..."
                      value={filtroParaAbrir}
                      onChange={(e) => setFiltroParaAbrir(e.target.value)}
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 text-sm"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {categorias
                      .filter(cat => !clasesAbiertas.find(c => c.categoria_id === cat.id))
                      .filter(cat => cat.nombre.toLowerCase().includes(filtroParaAbrir.toLowerCase()))
                      .map((cat) => {
                        const estadoHorario = getEstadoHorarioHoy(cat.horarios);
                        
                        return (
                          <Card key={cat.id} className={`p-3 sm:p-4 border-slate-700 ${estadoHorario?.tipo === 'con_retraso' ? 'bg-amber-900/20 border-amber-700/50' : 'bg-slate-800'}`}>
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-3 mb-3">
                              <div className="min-w-0">
                                <h3 className="font-semibold text-white text-sm sm:text-base">{cat.nombre}</h3>
                                {cat.horarios?.length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                    {cat.horarios.map((h, i) => (
                                      <p key={i} className="text-xs text-slate-500">
                                        {DIAS_SEMANA[h.dia]} {h.hora_inicio}-{h.hora_fin}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1 sm:flex-col sm:items-end">
                                {estadoHorario?.tipo === 'por_comenzar' && (
                                  <Badge className="bg-blue-600/80 text-xs whitespace-nowrap">Comienza {estadoHorario.hora}</Badge>
                                )}
                                {estadoHorario?.tipo === 'con_retraso' && (
                                  <Badge className="bg-amber-600 text-xs whitespace-nowrap">Retraso {estadoHorario.hora}</Badge>
                                )}
                              </div>
                            </div>
                            
                            <Button onClick={() => handleIniciarClase(cat.id)} className="w-full bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-sm sm:text-base">
                              <Play className="w-4 h-4 mr-2" /> Iniciar Clase
                            </Button>
                          </Card>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Historial */}
        {activeTab === 'historial' && (
          <div className="space-y-3 sm:space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-white">Historial de Clases</h2>
            
            {historialClases.length === 0 ? (
              <Card className="p-8 bg-slate-800 border-slate-700 text-center">
                <History className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-sm">No hay clases registradas</p>
              </Card>
            ) : (
              <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                <div className="divide-y divide-slate-700">
                  {historialClases.slice(0, 20).map(clase => (
                    <div key={clase.id} className="p-3 sm:p-4 hover:bg-slate-700/30 cursor-pointer active:bg-slate-700/50" onClick={() => handleAbrirClase(clase.id)}>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-white text-sm sm:text-base truncate">{clase.categoria_nombre}</p>
                          <p className="text-xs sm:text-sm text-slate-400">{clase.fecha} • {clase.hora_inicio} - {clase.hora_fin || '--:--'}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 text-xs">
                            {clase.total_presentes || 0} presentes
                          </Badge>
                          <Badge className={`text-xs ${clase.estado === 'abierta' ? 'bg-emerald-600' : 'bg-slate-600'}`}>
                            {clase.estado === 'abierta' ? 'En Curso' : 'Cerrada'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Modal de Clase - Mobile Optimized */}
      <Dialog open={showClaseModal} onOpenChange={setShowClaseModal}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto w-[calc(100%-1rem)] sm:w-full">
          <DialogHeader>
            <div className="flex items-start sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <DialogTitle className="text-lg sm:text-xl truncate">
                  {claseActiva?.categoria?.nombre || 'Clase'}
                </DialogTitle>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  {claseActiva?.fecha} • {claseActiva?.hora_inicio} - {claseActiva?.hora_fin || 'En curso'}
                </p>
              </div>
              <Badge className={`flex-shrink-0 ${claseActiva?.estado === 'abierta' ? 'bg-emerald-600' : 'bg-slate-600'}`}>
                {claseActiva?.estado === 'abierta' ? 'En Curso' : 'Cerrada'}
              </Badge>
            </div>
          </DialogHeader>

          {claseActiva && (
            <div className="space-y-4 sm:space-y-6 mt-4">
              {/* Info de entrenadores */}
              <div className="bg-slate-900/50 p-2 sm:p-3 rounded-lg space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-xs sm:text-sm text-slate-400">Encargado:</span>
                  <span className="text-white font-medium text-sm sm:text-base">
                    {claseActiva.entrenador_principal?.nombre} {claseActiva.entrenador_principal?.apellido}
                  </span>
                </div>
                
                {/* Entrenadores participantes */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-xs sm:text-sm text-slate-400">Participantes:</span>
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                    {claseActiva.entrenadores_asistentes?.length > 0 ? (
                      claseActiva.entrenadores_asistentes.map(ent => (
                        <div key={ent.id} className="flex items-center gap-1 bg-slate-700 px-2 py-1 rounded text-xs sm:text-sm">
                          <span>{ent.nombre}</span>
                          {claseActiva.estado === 'abierta' && (
                            <button onClick={() => handleQuitarEntrenadorClase(ent.id)} className="text-red-400 hover:text-red-300 ml-1">
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="text-slate-500 text-xs sm:text-sm">Ninguno</span>
                    )}
                    {claseActiva.estado === 'abierta' && (
                      <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 h-7 px-2" onClick={() => setShowAgregarEntrenador(true)}>
                        <UserPlus className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Hora de término */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mt-2 pt-2 border-t border-slate-700">
                  <span className="text-xs sm:text-sm text-slate-400">Hora término:</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={horaFinTemp}
                      onChange={(e) => setHoraFinTemp(e.target.value)}
                      className="bg-slate-700 border-slate-600 w-24 sm:w-28 h-8 text-sm"
                      disabled={claseActiva.estado !== 'abierta'}
                    />
                    {claseActiva.estado === 'abierta' && horaFinTemp && (
                      <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 h-8" onClick={handleGuardarHoraFin}>
                        <Save className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Asistencia */}
              <div>
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <h3 className="font-semibold text-white flex items-center gap-2 text-sm sm:text-base">
                    <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                    Asistencia ({Object.values(asistenciasTemp).filter(v => v).length}/{claseActiva.alumnos?.length || 0})
                  </h3>
                  {claseActiva.estado === 'abierta' && (
                    <Button onClick={handleGuardarAsistencia} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm">
                      <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Guardar
                    </Button>
                  )}
                </div>
                
                <div className="bg-slate-900/50 rounded-lg divide-y divide-slate-700/50">
                  {claseActiva.alumnos?.length === 0 ? (
                    <p className="text-slate-500 text-center py-4 text-sm">No hay alumnos</p>
                  ) : (
                    claseActiva.alumnos?.map(alumno => (
                      <div key={alumno.id} className="flex items-center justify-between p-2 sm:p-3">
                        <div className="min-w-0 mr-2">
                          <p className="text-white text-sm sm:text-base truncate">{alumno.nombre} {alumno.apellido}</p>
                          {alumno.estado_pago === 'moroso' && (
                            <span className="text-xs text-red-400 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Moroso
                            </span>
                          )}
                          {alumno.estado_pago === 'pendiente' && (
                            <span className="text-xs text-amber-400 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Pendiente
                            </span>
                          )}
                        </div>
                        {claseActiva.estado === 'abierta' ? (
                          <button
                            onClick={() => setAsistenciasTemp({ ...asistenciasTemp, [alumno.id]: !asistenciasTemp[alumno.id] })}
                            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors flex-shrink-0 active:scale-95 ${
                              asistenciasTemp[alumno.id] ? 'bg-emerald-600' : 'bg-slate-600'
                            }`}
                          >
                            {asistenciasTemp[alumno.id] ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : <X className="w-4 h-4 sm:w-5 sm:h-5" />}
                          </button>
                        ) : (
                          <span className={`flex-shrink-0 ${alumno.presente ? 'text-emerald-400 text-lg sm:text-xl' : 'text-red-400 text-lg sm:text-xl'}`}>
                            {alumno.presente ? '✓' : '✗'}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Notas */}
              <div>
                <h3 className="font-semibold text-white flex items-center gap-2 mb-2 sm:mb-3 text-sm sm:text-base">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
                  Notas y Observaciones
                </h3>
                
                {claseActiva.estado === 'abierta' && (
                  <div className="space-y-2 mb-3">
                    <div className="flex gap-2">
                      <Select value={tipoNotaTemp} onValueChange={setTipoNotaTemp}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          {TIPOS_NOTA.map(t => (
                            <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Textarea
                        value={notaTemp}
                        onChange={(e) => setNotaTemp(e.target.value)}
                        placeholder="Agregar nota..."
                        className="bg-slate-700 border-slate-600 text-white"
                        rows={2}
                      />
                      <Button onClick={handleAgregarNota} className="bg-amber-600 hover:bg-amber-700 px-4" disabled={!notaTemp.trim()}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  {claseActiva.notas?.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-2">Sin notas</p>
                  ) : (
                    claseActiva.notas?.map(nota => {
                      const tipoInfo = TIPOS_NOTA.find(t => t.value === nota.tipo) || TIPOS_NOTA[4];
                      const esMiNota = esAutorNota(nota);
                      
                      // Modo edición
                      if (editandoNotaId === nota.id) {
                        return (
                          <div key={nota.id} className="bg-slate-700/50 p-3 rounded-lg border border-amber-600/50">
                            <div className="space-y-2">
                              <Select value={editNotaTipo} onValueChange={setEditNotaTipo}>
                                <SelectTrigger className="bg-slate-700 border-slate-600 text-white h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-700 border-slate-600">
                                  {TIPOS_NOTA.map(t => (
                                    <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Textarea
                                value={editNotaContenido}
                                onChange={(e) => setEditNotaContenido(e.target.value)}
                                className="bg-slate-700 border-slate-600 text-white text-sm"
                                rows={2}
                              />
                              <div className="flex gap-2 justify-end">
                                <Button size="sm" variant="outline" onClick={cancelarEdicionNota}>
                                  <X className="w-3 h-3 mr-1" /> Cancelar
                                </Button>
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleEditarNota(nota.id)}>
                                  <Save className="w-3 h-3 mr-1" /> Guardar
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      // Modo visualización
                      return (
                        <div key={nota.id} className={`bg-slate-700/30 p-3 rounded-lg ${esMiNota ? 'border-l-2 border-emerald-500' : ''}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={`${tipoInfo.color} text-xs`}>{tipoInfo.label}</Badge>
                                {esMiNota && <span className="text-xs text-emerald-400">(Tu nota)</span>}
                              </div>
                              <p className="text-slate-300 text-sm">{nota.contenido}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {new Date(nota.created_at).toLocaleString('es-CL')}
                                {nota.editado_at && <span className="ml-2 text-amber-400">(editado)</span>}
                              </p>
                            </div>
                            {esMiNota && claseActiva.estado === 'abierta' && (
                              <div className="flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-600"
                                  onClick={() => iniciarEdicionNota(nota)}
                                >
                                  <FileText className="w-3 h-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-red-400 hover:bg-slate-600"
                                  onClick={() => handleEliminarNota(nota.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              
              {/* Acciones */}
              <div className="flex gap-2 pt-4 border-t border-slate-700">
                <Button onClick={() => setShowClaseModal(false)} variant="outline" className="flex-1">
                  Cerrar
                </Button>
                {claseActiva.estado === 'abierta' && esEntrenadorPrincipal() && (
                  <Button onClick={handleCerrarClase} className="flex-1 bg-red-600 hover:bg-red-700">
                    <Square className="w-4 h-4 mr-2" /> Finalizar Clase
                  </Button>
                )}
                {claseActiva.estado === 'abierta' && !esEntrenadorPrincipal() && (
                  <p className="text-xs text-slate-500 italic self-center">Solo el entrenador principal puede finalizar la clase</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Agregar Entrenador a Clase */}
      <Dialog open={showAgregarEntrenador} onOpenChange={setShowAgregarEntrenador}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Agregar Entrenador Participante</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {entrenadoresDisponibles
              .filter(e => e.id !== claseActiva?.entrenador_principal_id && !claseActiva?.entrenadores_asistentes_ids?.includes(e.id))
              .map(ent => (
                <Card 
                  key={ent.id} 
                  className="p-3 bg-slate-700 border-slate-600 hover:border-emerald-500 cursor-pointer transition-colors"
                  onClick={() => handleAgregarEntrenadorClase(ent.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">{ent.nombre} {ent.apellido}</p>
                      <p className="text-xs text-slate-400">{ent.email}</p>
                    </div>
                    <Plus className="w-5 h-5 text-emerald-400" />
                  </div>
                </Card>
              ))}
            {entrenadoresDisponibles.filter(e => e.id !== claseActiva?.entrenador_principal_id && !claseActiva?.entrenadores_asistentes_ids?.includes(e.id)).length === 0 && (
              <p className="text-slate-400 text-center py-4">No hay más entrenadores disponibles</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
